import { type FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { playAlertSound, type BreakingAlertLevel } from '@/algorithms/breaking-alert';

// ---------------------------------------------------------------------------
// Breaking Alert Overlay
// Displays FLASH / BREAKING / WATCH alerts from social signals in real-time
// ---------------------------------------------------------------------------

const LEVEL_CONFIG: Record<BreakingAlertLevel, {
  bg: string; border: string; title: string; icon: string; glow: string;
}> = {
  flash: {
    bg: 'bg-red-950/95',
    border: 'border-red-500',
    title: 'text-red-300',
    icon: '⚡',
    glow: 'shadow-[0_0_20px_rgba(239,68,68,0.6)]',
  },
  breaking: {
    bg: 'bg-orange-950/95',
    border: 'border-orange-500',
    title: 'text-orange-300',
    icon: '🔴',
    glow: 'shadow-[0_0_12px_rgba(249,115,22,0.4)]',
  },
  watch: {
    bg: 'bg-yellow-950/90',
    border: 'border-yellow-600',
    title: 'text-yellow-300',
    icon: '🟡',
    glow: '',
  },
};

const AUTO_DISMISS_MS = 30 * 60_000; // 30 minutes
const HISTORY_WINDOW_MS = 4 * 60 * 60_000; // show dismissed alerts for 4 hours

export const BreakingAlertOverlay: FC = () => {
  const breakingAlerts = useAppStore((s) => s.breakingAlerts);
  const dismissBreakingAlert = useAppStore((s) => s.dismissBreakingAlert);
  const markAudioPlayed = useAppStore((s) => s.markBreakingAlertAudioPlayed);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [, setTick] = useState(0); // 1-min ticker to refresh relative timestamps
  const prevCountRef = useRef(0);

  // Refresh "X ago" timestamps every 60s
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Auto-dismiss alerts older than 30 minutes
  useEffect(() => {
    const stale = breakingAlerts.filter(
      (a) => !a.dismissed && Date.now() - new Date(a.timestamp).getTime() > AUTO_DISMISS_MS,
    );
    stale.forEach((a) => dismissBreakingAlert(a.id));
  }, [breakingAlerts, dismissBreakingAlert]);

  // Show up to 3 un-dismissed alerts, most recent first
  const active = breakingAlerts
    .filter((a) => !a.dismissed)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 3);

  // Recently dismissed (within 4h) — for history tray
  const recentDismissed = breakingAlerts
    .filter((a) => a.dismissed && Date.now() - new Date(a.timestamp).getTime() < HISTORY_WINDOW_MS)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 10);

  // Play sound for new alerts
  useEffect(() => {
    const newAlerts = active.filter((a) => !a.audioPlayed);
    if (newAlerts.length === 0) return;

    const topNew = newAlerts.find((a) => a.level === 'flash')
      ?? newAlerts.find((a) => a.level === 'breaking')
      ?? newAlerts[0];

    if (topNew) {
      playAlertSound(topNew.level);
      newAlerts.forEach((a) => markAudioPlayed(a.id));
    }

    prevCountRef.current = active.length;
  }, [active.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close history tray when new active alert arrives
  useEffect(() => {
    if (active.length > 0) setHistoryOpen(false);
  }, [active.length]);

  if (active.length === 0 && recentDismissed.length === 0) return null;

  return (
    <div className="absolute top-0 z-[150] pointer-events-none left-0 right-0 md:left-80 md:right-[42%] lg:left-96 lg:right-[38%]">
      <div className="flex flex-col gap-1 px-3 pt-1 pointer-events-auto">

        {/* ── Active alerts ──────────────────────────────────────────────── */}
        {active.map((alert, idx) => {
          const c = LEVEL_CONFIG[alert.level];
          const isTop = idx === 0;
          const isExpanded = expanded === alert.id;

          return (
            <div
              key={alert.id}
              className={clsx(
                'relative rounded border backdrop-blur-md transition-all duration-300',
                c.bg, c.border, c.glow,
                isTop && alert.level === 'flash' && 'animate-pulse',
                !isTop && 'opacity-80',
              )}
            >
              <div className="flex items-start gap-2 px-3 py-2">
                <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                  <span className="text-sm">{c.icon}</span>
                  <span className={clsx('text-[9px] font-black uppercase tracking-widest', c.title)}>
                    {alert.level === 'flash' ? 'FLASH' : alert.level === 'breaking' ? 'BREAKING' : 'WATCH'}
                  </span>
                </div>
                <div className="w-px h-4 bg-slate-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className={clsx('text-[11px] font-semibold leading-snug', c.title)}>
                    {alert.title}
                  </p>
                  {isExpanded && (
                    <p className="text-[10px] text-slate-300 mt-1 leading-relaxed">
                      {alert.content}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] text-slate-500">{alert.source}</span>
                    <span className="text-[9px] text-slate-600">·</span>
                    <span className="text-[9px] text-slate-500 tabular-nums">
                      {formatRelativeTime(alert.timestamp)}
                    </span>
                    {alert.keywords.length > 0 && (
                      <>
                        <span className="text-[9px] text-slate-600">·</span>
                        <span className={clsx('text-[9px] font-medium', c.title)}>
                          {alert.keywords[0]}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setExpanded(isExpanded ? null : alert.id)}
                    className="text-[10px] px-1.5 py-0.5 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/40 transition-colors"
                  >
                    {isExpanded ? '▲' : '▼'}
                  </button>
                  <button
                    onClick={() => dismissBreakingAlert(alert.id)}
                    className="text-[10px] px-1.5 py-0.5 rounded text-slate-500 hover:text-slate-200 hover:bg-slate-700/40 transition-colors"
                    title="Dismiss (recoverable from history)"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {alert.level === 'flash' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l animate-pulse" />
              )}
            </div>
          );
        })}

        {/* ── "N more" badge ─────────────────────────────────────────────── */}
        {breakingAlerts.filter((a) => !a.dismissed).length > 3 && (
          <div className="text-[9px] text-slate-500 pl-2">
            +{breakingAlerts.filter((a) => !a.dismissed).length - 3} more alerts
          </div>
        )}

        {/* ── FLASH pulsing dot ──────────────────────────────────────────── */}
        {active.length > 0 && active[0].level === 'flash' && (
          <div className="absolute top-2 right-20 flex items-center gap-1 pointer-events-none">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
          </div>
        )}

        {/* ── History tray (shown when no active alerts, dismissed exist) ── */}
        {active.length === 0 && recentDismissed.length > 0 && (
          <div className="rounded border border-slate-700/60 bg-slate-900/80 backdrop-blur-md overflow-hidden">
            {/* Tray toggle row */}
            <button
              onClick={() => setHistoryOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-800/40 transition-colors"
            >
              <span className="text-[10px] text-slate-500">📋</span>
              <span className="text-[9px] text-slate-500 flex-1">
                {recentDismissed.length} recent alert{recentDismissed.length > 1 ? 's' : ''} dismissed
              </span>
              <span className="text-[9px] text-slate-600">{historyOpen ? '▲' : '▼'}</span>
            </button>

            {/* Dismissed alert list */}
            {historyOpen && (
              <div className="border-t border-slate-700/40 max-h-52 overflow-y-auto">
                {recentDismissed.map((alert) => {
                  const c = LEVEL_CONFIG[alert.level];
                  return (
                    <div
                      key={alert.id}
                      className="flex items-start gap-2 px-3 py-2 border-b border-slate-800/50 last:border-0 opacity-60 hover:opacity-90 transition-opacity"
                    >
                      <span className="text-[10px] shrink-0 mt-0.5">{c.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className={clsx('text-[10px] font-semibold leading-snug truncate', c.title)}>
                          {alert.title}
                        </p>
                        <p className="text-[9px] text-slate-500 mt-0.5 leading-snug line-clamp-2">
                          {alert.content}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[9px] text-slate-600">{alert.source}</span>
                          <span className="text-[9px] text-slate-700">·</span>
                          <span className="text-[9px] text-slate-600 tabular-nums">
                            {formatRelativeTime(alert.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function formatRelativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
